import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { EndUser } from '@app/core/interfaces/c5/end-user';
import { FeatureName } from '@app/core/interfaces/c5/feature-name';
import { NumberRange } from '@app/core/interfaces/c5/number-range';
import { ScreenNumber } from '@app/core/interfaces/screen-number';
import { TrunkCommon } from '@app/core/interfaces/trunk-common';
import { isTeamsTrunk, TrunkProfile } from '@app/core/interfaces/trunk-profile';
import { SecurityPermissionName } from '@app/core/models/permission';
import { TrunkRepository } from '@app/core/repositories/trunk.repository';
import { PhoneNumberLike } from '@app/shared/models/phone-number-like';
import { TeamsTrunkAssignmentState } from '@app/shared/models/teams-trunk-assignment-state';
import { TrunkProfileDifference } from '@app/shared/models/trunk-profile-difference';
import { sortBy } from '@app/utils/sort/sort-by';
import { ExtendedFeatureProfile } from '@business/feature/interfaces/feature-profile';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TrunkUtilService {
  /**
   * These fields can be reset by the user if they differ from
   * the default value that is defined by the chosen profile.
   */
  resettableAdvancedFields = ['forceRingback', 'receiveUserProvidedNum', 'strategy'];

  constructor(
    private authenticationService: AuthenticationService,
    private trunkRepository: TrunkRepository
  ) {}

  calculateAdvancedSettingsDifferences(
    trunk: TrunkCommon,
    trunkProfile: TrunkProfile
  ): TrunkProfileDifference[] {
    if (trunkProfile == null) {
      return [];
    }

    return this.resettableAdvancedFields
      .map((field): TrunkProfileDifference => {
        const current = trunk.c5[field];
        const profile = trunkProfile.json.specialTarget[field];
        const valuesDiffer = current !== profile;

        if (valuesDiffer) {
          return {
            current,
            field,
            profile,
          };
        } else {
          return null;
        }
      })
      .filter((difference) => difference != null);
  }

  calculateDifferences(trunk: TrunkCommon, profile: TrunkProfile): TrunkProfileDifference[] {
    if (profile == null) {
      return [];
    }

    const ddiDifferences = Object.entries(profile.json.ddiData)
      .map(([key, value]) => {
        const current = trunk.c5.ddiData[key];

        if (current !== value) {
          return {
            current,
            field: `ddi.${key}`,
            profile: value,
          };
        } else {
          return null;
        }
      })
      .filter((difference) => difference != null);

    const specialTargetDifferences = Object.entries(profile.json.specialTarget)
      .map(([key, value]) => {
        const current = trunk.c5.specialTarget[key];
        const valuesDiffer = current !== value;

        if (this.ignoreProperty(key) === false && valuesDiffer) {
          return {
            current,
            field: `specialTarget.${key}`,
            profile: value,
          };
        } else {
          return null;
        }
      })
      .filter((difference) => difference != null);

    return specialTargetDifferences.concat(ddiDifferences).sort(sortBy('field'));
  }

  checkTeamsTrunkAssignment$(
    endUser: EndUser,
    featureProfile: ExtendedFeatureProfile
  ): Observable<TeamsTrunkAssignmentState | null> {
    let result$: Observable<TeamsTrunkAssignmentState | null> = of(null);

    const isAllowedTrunkAccess: Boolean = this.authenticationService.hasPermission(
      SecurityPermissionName.VIEW_TRUNKS
    );
    const isAssignedToTrunk: Boolean = endUser.mainUserId > 0;
    const hasTeamsEnabled = featureProfile.optionalFeatures.find(
      (feature) => feature.name === FeatureName.MICROSOFT_TEAMS
    )?.userOptionalFeatureActive;

    if (hasTeamsEnabled) {
      if (isAssignedToTrunk === false) {
        result$ = of(TeamsTrunkAssignmentState.Unassigned);
      } else if (isAllowedTrunkAccess) {
        result$ = this.trunkRepository.get(endUser.mainUserId).pipe(
          map((assignedTrunk) => {
            let state: TeamsTrunkAssignmentState | null = null;

            const isAssignedToTeamsTrunk = isTeamsTrunk(assignedTrunk.db);

            if (assignedTrunk == null) {
              state = TeamsTrunkAssignmentState.Unassigned;
            } else if (hasTeamsEnabled) {
              if (isAssignedToTeamsTrunk === false && assignedTrunk != null) {
                state = TeamsTrunkAssignmentState.AssignedTrunkIsNotATeamsTrunk;
              } else {
                state = TeamsTrunkAssignmentState.AssignedToTeamsTrunk;
              }
            } else if (assignedTrunk != null && isAssignedToTeamsTrunk) {
              state = TeamsTrunkAssignmentState.AssignedToTeamsTrunkWithoutEnabledOptionalFeature;
            } else if (assignedTrunk == null) {
              state = TeamsTrunkAssignmentState.Unassigned;
            }

            return state;
          })
        );
      }
    }

    return result$;
  }

  /**
   * Returns a list of screen numbers that are not used as a number range. You
   * can pass an id of a number range that will not be checked (e.g. when this
   * number range is being edited and the matching screen number should be
   * available).
   *
   * @param entryId Id of a number range that should be ignored
   * @param screenNumbers List of available screen numbers that is filtered
   * @param numberRanges List of existing number ranges that is checked for matches against the screen numbers
   */
  filterAvailableScreenNumber({
    entryId,
    numberRanges,
    screenNumbers,
  }: {
    entryId?: number;
    numberRanges: NumberRange[];
    screenNumbers: ScreenNumber[];
  }): ScreenNumber[] {
    return screenNumbers.filter((screenNumber) => {
      const screenNumberIsInUse: boolean = numberRanges.some((numberRange) => {
        if (entryId != null && numberRange.id === entryId) {
          return false;
        }

        return this.numberMatchesNumberRange(screenNumber, numberRange);
      });

      return screenNumberIsInUse === false;
    });
  }

  /**
   * Check if the given phone number like object matches the given number range
   * (by comparing the startCc, startNdc and startSn).
   *
   * @param number Phone number like object containing at least cc and sn.
   * @param numberRange A trunk number range to compare against.
   */
  numberMatchesNumberRange(number: PhoneNumberLike, numberRange: NumberRange): boolean {
    return (
      number.cc === numberRange.startCc &&
      (number.ndc ?? '') === (numberRange.startNdc ?? '') &&
      number.sn.startsWith(numberRange.startSn)
    );
  }

  private ignoreProperty(property: string): boolean {
    return ['strategy', 'receiveUserProvidedNum', 'forceRingback', 'msTeam', 'maxCalls'].includes(property);
  }
}
